import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Print as PrintIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import usePageTitle from '../../utils/usePageTitle';

const DeliveryRequestDetails = () => {
  const { projectCode } = useParams();
  usePageTitle(`Delivery Request - ${projectCode}`);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [drData, setDrData] = useState([]);
  const [projectInfo, setProjectInfo] = useState({});
  const [adminEmail, setAdminEmail] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [currentSearchField, setCurrentSearchField] = useState({ drIndex: 0, detailIndex: 0 });
  const [isSaved, setIsSaved] = useState({});

  useEffect(() => {
    if (successMessage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const createEmptyDRDetail = () => ({
    id: Date.now() + Math.random(),
    modelName: '',
    modelDtlId: 0,
    totalDelReqQTY: 0,
    aavlDelReqQTY: 0,
    bavlDelReqQTY: 0,
    eavlDelReqQTY: 0,
    totalRecievedQTY: 0,
    aavlRecievedQTY: 0,
    bavlRecievedQTY: 0,
    eavlRecievedQTY: 0,
    totalPrevRecQTY: 0,
    aavlPrevRecQTY: 0,
    bavlPrevRecQTY: 0,
    eavlPrevRecQTY: 0,
    projectQTY: 0
  });

  const createEmptyDRFallback = () => ({
    id: 1,
    code: projectCode,
    lotNumber: '',
    requestedDate: '',
    receivedDate: '',
    status: 'ACT',
    materials: '',
    note: '',
    detailsList: [createEmptyDRDetail()]
  });

  const loadDRData = useCallback(async (keepSavedStates = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading DR data for project:', projectCode);
      
      const response = await api.get(`/delivery-request/${projectCode}`);
      const data = response.data;
      console.log('DR API response received');
      
      if (data.error) {
        setError(`Database error: ${data.error}`);
      }
      
      setDrData(data.drList || []);
      setProjectInfo(data.projectInfo || { code: projectCode, status: 'ACT' });
      setAdminEmail(data.adminEmail || '');
      
      // Reset isSaved for all DRs only if not preserving state
      if (!keepSavedStates) {
        const savedState = {};
        (data.drList || []).forEach((dr, index) => {
          savedState[index] = false;
        });
        setIsSaved(savedState);
      }
      
      console.log('Successfully loaded DR data');
      
    } catch (err) {
      console.error('DR load error:', err);
      setError(`Failed to load delivery request data: ${err.message}`);
      
      // Create fallback empty structure
      setDrData([createEmptyDRFallback()]);
      setProjectInfo({ code: projectCode, status: 'ACT' });
      setAdminEmail('');
      
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCode]);

  useEffect(() => {
    loadDRData();
  }, [loadDRData]);

  const handleInputChange = (drIndex, detailIndex, field, value) => {
    const updatedData = [...drData];
    if (detailIndex !== null) {
      updatedData[drIndex].detailsList[detailIndex][field] = value;
      // Calculate totals for quantity fields
      if (['aavlDelReqQTY', 'bavlDelReqQTY', 'eavlDelReqQTY'].includes(field)) {
        const detail = updatedData[drIndex].detailsList[detailIndex];
        detail.totalDelReqQTY = (detail.aavlDelReqQTY || 0) + (detail.bavlDelReqQTY || 0) + (detail.eavlDelReqQTY || 0);
        
        if (detail.totalDelReqQTY !== detail.projectQTY && detail.totalDelReqQTY > 0) {
          setError(`Total DR Qty (${detail.totalDelReqQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
          return;
        }
      }
      if (['aavlRecievedQTY', 'bavlRecievedQTY', 'eavlRecievedQTY'].includes(field)) {
        const detail = updatedData[drIndex].detailsList[detailIndex];
        detail.totalRecievedQTY = (detail.aavlRecievedQTY || 0) + (detail.bavlRecievedQTY || 0) + (detail.eavlRecievedQTY || 0);
        
        if (detail.totalRecievedQTY !== detail.projectQTY && detail.totalRecievedQTY > 0) {
          setError(`Total Received Qty (${detail.totalRecievedQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
          return;
        }
      }
    } else {
      updatedData[drIndex][field] = value;
    }
    setError(null);
    setDrData(updatedData);
    setIsSaved(prev => ({ ...prev, [drIndex]: false }));
  };

  const handleAddRow = (drIndex) => {
    const updatedData = [...drData];
    updatedData[drIndex].detailsList.push(createEmptyDRDetail());
    setDrData(updatedData);
  };

  const handleDeleteRow = (drIndex) => {
    if (selectedRows.length === 0) return;
    const updatedData = [...drData];
    updatedData[drIndex].detailsList = updatedData[drIndex].detailsList.filter(
      (_, index) => !selectedRows.includes(`${drIndex}-${index}`)
    );
    setDrData(updatedData);
    setSelectedRows([]);
  };

  const handleSave = async (drIndex) => {
    try {
      const dr = drData[drIndex];
      
      // Validation
      if (!dr.receivedDate) {
        setError('Please select Received Date');
        return;
      }
      
      const hasValidDetails = dr.detailsList.some(detail => 
        detail.modelName && (detail.aavlDelReqQTY > 0 || detail.bavlDelReqQTY > 0 || detail.eavlDelReqQTY > 0)
      );
      
      if (!hasValidDetails) {
        setError('Please add at least one model with delivery request quantity');
        return;
      }
      
      // Validate quantities equal project quantities
      for (const detail of dr.detailsList) {
        if (detail.modelDtlId && detail.totalDelReqQTY > 0 && detail.totalDelReqQTY !== detail.projectQTY) {
          setError(`Total DR Qty (${detail.totalDelReqQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
          return;
        }
        if (detail.modelDtlId && detail.totalRecievedQTY > 0 && detail.totalRecievedQTY !== detail.projectQTY) {
          setError(`Total Received Qty (${detail.totalRecievedQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
          return;
        }
      }
      
      setLoading(true);
      setError(null);
      const response = await api.post(`/delivery-request/${projectCode}/save`, drData[drIndex]);
      setSuccessMessage(`ISSUED - Delivery Request has been issued successfully. Lot Number: ${response.data.lotNumber}`);
      setIsSaved(prev => ({ ...prev, [drIndex]: true }));
      await loadDRData(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save delivery request. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (drIndex) => {
    const dr = drData[drIndex];
    
    // Validation
    if (!dr.receivedDate) {
      setError('Please select Received Date for confirmation');
      return;
    }
    
    if (!adminEmail) {
      setError('Please enter Admin Email for confirmation');
      return;
    }
    
    const hasValidDetails = dr.detailsList.some(detail => 
      detail.modelName && (detail.aavlRecievedQTY > 0 || detail.bavlRecievedQTY > 0 || detail.eavlRecievedQTY > 0)
    );
    
    if (!hasValidDetails) {
      setError('Please add at least one model with received quantity');
      return;
    }
    
    // Validate quantities equal project quantities
    for (const detail of dr.detailsList) {
      if (detail.modelDtlId && detail.totalDelReqQTY > 0 && detail.totalDelReqQTY !== detail.projectQTY) {
        setError(`Total DR Qty (${detail.totalDelReqQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
        return;
      }
      if (detail.modelDtlId && detail.totalRecievedQTY > 0 && detail.totalRecievedQTY !== detail.projectQTY) {
        setError(`Total Received Qty (${detail.totalRecievedQTY}) must equal Project Qty (${detail.projectQTY}) for model ${detail.modelName}`);
        return;
      }
    }
    
    if (!window.confirm('Are you sure you want to confirm this Delivery Request? This will send email notifications to admins.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(`/delivery-request/${projectCode}/confirm`, {
        ...dr,
        adminEmail: adminEmail
      });
      setSuccessMessage(`CONFIRMED - DR confirmed successfully. Status: CON | Lot Number: ${response.data.lotNumber}`);
      await loadDRData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to confirm delivery request. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (drIndex) => {
    const dr = drData[drIndex];
    
    if (!dr.lotNumber) {
      setError('Lot number required. Please save/confirm DR first.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/delivery-request/${projectCode}/print/${dr.lotNumber}`);
      
      if (response.data.success) {
        openPrintWindow(response.data);
      }
    } catch (err) {
      setError(`Print failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openPrintWindow = (printData) => {
    const { headerInfo, currentData, previousData } = printData;
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Delivery Request - ${headerInfo.projectCode}</title>
        <style>
          body { font-family: Arial; margin: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #000; padding: 8px; text-align: center; }
          th { background-color: #f0f0f0; font-weight: bold; }
          .model-cell { text-align: left; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>DELIVERY REQUEST</h2>
          <p><strong>Project Code:</strong> ${headerInfo.projectCode}</p>
          <p><strong>Lot Number:</strong> ${headerInfo.lotNumber}</p>
          <p><strong>Requested:</strong> ${headerInfo.requestedDate} | 
             <strong>Received:</strong> ${headerInfo.receivedDate}</p>
        </div>
        
        <h3>Current Delivery</h3>
        <table>
          <thead>
            <tr>
              <th rowspan="2">Model</th>
              <th colspan="4">DR Qty</th>
              <th colspan="4">Received Qty</th>
              <th rowspan="2">Project Qty</th>
            </tr>
            <tr>
              <th>Total</th><th>A</th><th>B</th><th>E</th>
              <th>Total</th><th>A</th><th>B</th><th>E</th>
            </tr>
          </thead>
          <tbody>
            ${currentData.map(row => `
              <tr>
                <td class="model-cell">${row.modelName}</td>
                <td>${row.totalDrQty}</td><td>${row.aDrQty}</td><td>${row.bDrQty}</td><td>${row.eDrQty}</td>
                <td>${row.totalRecQty}</td><td>${row.aRecQty}</td><td>${row.bRecQty}</td><td>${row.eRecQty}</td>
                <td>${row.projectQty}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        ${previousData.length > 0 ? `
          <h3>Previous Deliveries</h3>
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Total Prev</th><th>A Prev</th><th>B Prev</th><th>E Prev</th>
              </tr>
            </thead>
            <tbody>
              ${previousData.map(row => `
                <tr>
                  <td class="model-cell">${row.modelName}</td>
                  <td>${row.totalPrevQty}</td><td>${row.aPrevQty}</td>
                  <td>${row.bPrevQty}</td><td>${row.ePrevQty}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        <div style="margin-top: 20px;">
          <p><strong>Materials:</strong> ${headerInfo.materials || 'N/A'}</p>
          <p><strong>Note:</strong> ${headerInfo.note || 'N/A'}</p>
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 30px;">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  const handleCreate = () => {
    // Create a new DR record directly in the frontend
    const newDRRecord = {
      id: Date.now(),
      code: projectCode,
      lotNumber: '',
      requestedDate: '',
      receivedDate: '',
      status: 'ACT',
      materials: '',
      note: '',
      detailsList: [createEmptyDRDetail()]
    };
    
    // Add the new DR record to the existing list
    setDrData(prevData => [...prevData, newDRRecord]);
  };

  const handleBack = () => {
    navigate('/delivery-request');
  };

  const handleSearchModels = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setModelOptions([]);
      return;
    }
    
    try {
      const response = await api.get(`/delivery-request/${projectCode}/search-models?search=${encodeURIComponent(searchTerm)}`);
      setModelOptions(response.data);
    } catch (err) {
      console.error('Model search error:', err);
    }
  };

  const handleModelSelect = (model) => {
    if (model) {
      const { drIndex, detailIndex } = currentSearchField;
      handleInputChange(drIndex, detailIndex, 'modelName', model.displayText);
      handleInputChange(drIndex, detailIndex, 'modelDtlId', model.modelDtlId);
      handleInputChange(drIndex, detailIndex, 'projectQTY', model.quantity);
      handleInputChange(drIndex, detailIndex, 'aavlDelReqQTY', model.quantity);
      handleInputChange(drIndex, detailIndex, 'bavlDelReqQTY', 0);
      handleInputChange(drIndex, detailIndex, 'eavlDelReqQTY', 0);
      handleInputChange(drIndex, detailIndex, 'aavlRecievedQTY', model.quantity);
      handleInputChange(drIndex, detailIndex, 'bavlRecievedQTY', 0);
      handleInputChange(drIndex, detailIndex, 'eavlRecievedQTY', 0);
      
      const updatedData = [...drData];
      const detail = updatedData[drIndex].detailsList[detailIndex];
      detail.totalDelReqQTY = model.quantity;
      detail.totalRecievedQTY = model.quantity;
      setDrData(updatedData);
      
      setSearchDialogOpen(false);
    }
  };

  const openSearchDialog = async (drIndex, detailIndex) => {
    setCurrentSearchField({ drIndex, detailIndex });
    setSearchDialogOpen(true);
    try {
      const response = await api.get(`/delivery-request/${projectCode}/search-models?search=`);
      setModelOptions(response.data);
    } catch (err) {
      console.error('Failed to load models:', err);
      setModelOptions([]);
    }
  };

  const isConfirmed = (dr) => {
    return dr.status === 'CON';
  };

  const isEditable = projectInfo.status === 'ACT' && projectInfo.typeOfWork !== 'RBX';

  if (loading) return <LinearProgress aria-label="Loading delivery request data" />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">Delivery Request - {projectCode}</Typography>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Project: {projectInfo.code} | Status: {drData[0]?.status || projectInfo.status} | Requestor: {projectInfo.requestorName}
      </Typography>

      {drData.map((dr, drIndex) => (
        <Paper key={dr.id} sx={{ mb: 4, p: 2 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Delivery Request {drIndex + 1}
          </Typography>
          
          {isConfirmed(dr) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>CONFIRMED</strong> - This delivery request is confirmed and cannot be edited.
              Status: CON | Lot Number: {dr.lotNumber}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'nowrap' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography sx={{ whiteSpace: 'nowrap' }}>Lot Number: {dr.lotNumber || 'Not assigned'}</Typography>
              <Typography sx={{ whiteSpace: 'nowrap' }}>Requested Date: {dr.requestedDate || 'Not set'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ whiteSpace: 'nowrap' }}>Received Date:</Typography>
              <TextField
                type="datetime-local"
                value={dr.receivedDate || ''}
                onChange={(e) => {
                  handleInputChange(drIndex, null, 'receivedDate', e.target.value);
                  e.target.blur();
                }}
                disabled={!isEditable || isConfirmed(dr)}
                size="small"
                label="Received Date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Received Date' }}
                sx={{ width: 220 }}
              />
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small" aria-label={`Delivery request ${drIndex + 1} details`}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>
                    <Checkbox
                      checked={dr.detailsList.every((_, i) => selectedRows.includes(`${drIndex}-${i}`))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows([...selectedRows, ...dr.detailsList.map((_, i) => `${drIndex}-${i}`)]);
                        } else {
                          setSelectedRows(selectedRows.filter(id => !id.startsWith(`${drIndex}-`)));
                        }
                      }}
                      inputProps={{ 'aria-label': `Select all rows in delivery request ${drIndex + 1}` }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>Model Code</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }} colSpan={4}>DR Qty</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }} colSpan={4}>Received Qty</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }} colSpan={4}>Prev Received Qty</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>Project Qty</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell component="th" scope="col" />
                  <TableCell component="th" scope="col" />
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>Total</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>A</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>B</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>E</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>Total</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>A</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>B</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>E</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>Total</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>A</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>B</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white' }}>E</TableCell>
                  <TableCell component="th" scope="col" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(dr.detailsList || []).map((detail, detailIndex) => (
                  <TableRow key={detail.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.includes(`${drIndex}-${detailIndex}`)}
                        onChange={(e) => {
                          const id = `${drIndex}-${detailIndex}`;
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, id]);
                          } else {
                            setSelectedRows(selectedRows.filter(rowId => rowId !== id));
                          }
                        }}
                        inputProps={{ 'aria-label': `Select row ${detailIndex + 1} for model ${detail.modelName || 'unnamed'}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          value={detail.modelName}
                          onChange={(e) => handleInputChange(drIndex, detailIndex, 'modelName', e.target.value)}
                          disabled={!isEditable || isConfirmed(dr)}
                          size="small"
                          inputProps={{ 'aria-label': `Model code for row ${detailIndex + 1}` }}
                          sx={{ width: 200 }}
                        />
                        {isEditable && !isConfirmed(dr) && (
                          <IconButton
                            size="small"
                            onClick={() => openSearchDialog(drIndex, detailIndex)}
                            title="Search Models"
                            aria-label={`Search models for row ${detailIndex + 1}`}
                          >
                            <SearchIcon aria-hidden="true" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.totalDelReqQTY}
                        disabled
                        size="small"
                        inputProps={{ 'aria-label': `Total DR quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.aavlDelReqQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'aavlDelReqQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `A-Available DR quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.bavlDelReqQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'bavlDelReqQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `B-Available DR quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.eavlDelReqQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'eavlDelReqQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `E-Available DR quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.totalRecievedQTY}
                        disabled
                        size="small"
                        inputProps={{ 'aria-label': `Total received quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.aavlRecievedQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'aavlRecievedQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `A-Available received quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.bavlRecievedQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'bavlRecievedQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `B-Available received quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.eavlRecievedQTY}
                        onChange={(e) => handleInputChange(drIndex, detailIndex, 'eavlRecievedQTY', parseInt(e.target.value) || 0)}
                        disabled={!isEditable || isConfirmed(dr)}
                        size="small"
                        inputProps={{ 'aria-label': `E-Available received quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>{detail.totalPrevRecQTY}</TableCell>
                    <TableCell>{detail.aavlPrevRecQTY}</TableCell>
                    <TableCell>{detail.bavlPrevRecQTY}</TableCell>
                    <TableCell>{detail.eavlPrevRecQTY}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detail.projectQTY}
                        disabled
                        size="small"
                        inputProps={{ 'aria-label': `Project quantity for row ${detailIndex + 1}` }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Materials (Max 200 characters)"
              multiline
              rows={2}
              value={dr.materials}
              onChange={(e) => {
                if (e.target.value.length <= 200) {
                  handleInputChange(drIndex, null, 'materials', e.target.value);
                }
              }}
              disabled={!isEditable}
              fullWidth
              sx={{ mb: 1 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${dr.materials.length}/200 characters`}
            />
            <TextField
              label="Note (Max 4000 characters)"
              multiline
              rows={3}
              value={dr.note}
              onChange={(e) => {
                if (e.target.value.length <= 4000) {
                  handleInputChange(drIndex, null, 'note', e.target.value);
                }
              }}
              disabled={!isEditable}
              fullWidth
              inputProps={{ maxLength: 4000 }}
              helperText={`${dr.note.length}/4000 characters`}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => handleSave(drIndex)}
              disabled={!isEditable || loading || isConfirmed(dr)}
              aria-label={`Issue delivery request ${drIndex + 1}`}
            >
              Issue
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon aria-hidden="true" />}
              onClick={() => handlePrint(drIndex)}
              aria-label={`Print delivery request ${drIndex + 1}`}
            >
              Print
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleConfirm(drIndex)}
              disabled={!isEditable || loading || isConfirmed(dr) || !dr.receivedDate || !adminEmail || !isSaved[drIndex]}
              title={isConfirmed(dr) ? 'Already confirmed - status is CON' : 'Confirm and send email to admins'}
              aria-label={isConfirmed(dr) ? `Delivery request ${drIndex + 1} already confirmed` : `Confirm and email delivery request ${drIndex + 1}`}
            >
              {isConfirmed(dr) ? 'CONFIRMED (CON)' : 'Confirm & Email'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon aria-hidden="true" />}
              onClick={() => handleAddRow(drIndex)}
              disabled={!isEditable || dr.lotNumber || isConfirmed(dr)}
              aria-label={`Add row to delivery request ${drIndex + 1}`}
            >
              Add Row
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon aria-hidden="true" />}
              onClick={() => handleDeleteRow(drIndex)}
              disabled={!isEditable || dr.lotNumber || selectedRows.length === 0 || isConfirmed(dr)}
              aria-label={`Delete selected rows from delivery request ${drIndex + 1}`}
            >
              Delete Row
            </Button>
          </Box>
        </Paper>
      ))}

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={loading}
          aria-label="Create new delivery request"
        >
          Create
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          aria-label="Go back to delivery request list"
        >
          Back
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <TextField
          label="Admin Email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          Note: Admin email will be used for notifications
        </Typography>
      </Box>

      {/* Model Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="md" fullWidth aria-labelledby="model-search-dialog-title">
        <DialogTitle id="model-search-dialog-title">Search Models</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={modelOptions}
            getOptionLabel={(option) => option.displayText || ''}
            onInputChange={(event, newInputValue) => {
              handleSearchModels(newInputValue);
            }}
            onChange={(event, newValue) => {
              handleModelSelect(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for models"
                placeholder="Type to search models..."
                fullWidth
                autoFocus
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.displayText}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quantity: {option.quantity}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="Type to search for models"
            loading={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryRequestDetails;